// Lang Imports
import type { UiLocale } from './locales'
import { DEFAULT_UI_LOCALE } from './locales'
import en from './en.json'
import mn from './mn.json'

/*
 * Хэлний сан — ХАЙЛТ.
 *
 * `i18n-provider.ts` нь Refine-ийн гэрээг (мэдэгдэл, resource-ийн шошго) хариуцна;
 * энэ файл нь ЗӨВХӨН «түлхүүр → мөр» хөрвүүлэлт. Хоёрыг тусад нь барьсан шалтгаан:
 * хайлтын дүрмийг (нөөц хэл, параметр, нэгтгэл) Refine-гүйгээр шалгаж болно.
 */

type Tree = { [key: string]: string | Tree }

/*
 * JSON нь УНШИХАД зориулж ҮҮРЛЭСЭН, хайхад зориулж ХАВТГАЙ.
 *
 * Refine нь `'notifications.createSuccess'` гэсэн НЭГ мөрөөр асуудаг тул модыг
 * түлхүүр бүрээр гүйлгэхийн оронд модуль ачаалагдахад нэг удаа хавтгайруулна.
 */
const flatten = (tree: Tree, prefix = '', out: Record<string, string> = {}): Record<string, string> => {
  for (const [name, value] of Object.entries(tree)) {
    const key = prefix ? `${prefix}.${name}` : name

    if (typeof value === 'string') out[key] = value
    else flatten(value, key, out)
  }

  return out
}

export const DICTIONARIES: Record<UiLocale, Record<string, string>> = {
  mn: flatten(mn as Tree),
  en: flatten(en as Tree)
}

/*
 * `{{name}}` — i18next-ийн хэлбэр.
 *
 * ! Хэлбэрийг ЗОРИУД i18next-тэй ижил авсан: хэлний сан томрох үед `@refinedev/i18n`
 * ! эсвэл i18next руу шилжихэд JSON-г ДАХИН БИЧИХ шаардлагагүй.
 */
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g

/**
 * Параметрийг тавина.
 *
 * ! Байхгүй параметрийг `{{name}}` хэлбэрээрээ ҮЛДЭЭНЭ — `undefined` гэж
 * ! бичихээс хамаагүй дээр: дэлгэц эвдрэхгүй, харин алдаа нь нүдэнд харагдана.
 */
export const interpolate = (template: string, params?: Record<string, unknown>): string => {
  if (!params) return template

  return template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = params[name]

    return value === undefined || value === null ? whole : String(value)
  })
}

/*
 * ОЛОН ТООНЫ ХЭЛБЭР — ХЭЛ БҮРИЙН ӨӨРИЙН асуудал.
 *
 * ! Монголд тооны дараа нэр УРАГШ ХУВИРДАГГҮЙ: «1 мөр», «5 мөр». Тиймээс `mn.json`-д
 * ! `_one`/`_other` хэлбэр БАЙХГҮЙ — нэг мөр хангалттай. Англид `1 row` / `5 rows`
 * ! гэж хувирдаг тул `en.json`-д хоёр хэлбэр бий.
 * ! ⇒ Хоёр файлын бүтэц ижил байх дүрмийн ЦОРЫН ГАНЦ зөвшөөрөгдсөн зөрүү нь энэ.
 * ! `i18n:check` нь хэлбэрийн дагаврыг хасаж тулгадаг.
 */
const entry = (dict: Record<string, string>, key: string, params?: Record<string, unknown>): string | undefined => {
  const count = params?.count

  if (typeof count === 'number') {
    const form = dict[`${key}_${count === 1 ? 'one' : 'other'}`]

    if (form !== undefined) return form
  }

  return dict[key]
}

/**
 * Түлхүүрийг тухайн хэлээр олно. Олдохгүй бол `undefined` — нөөцийг
 * дуудагч (`i18n-provider.ts`) шийднэ.
 *
 * Дараалал: тухайн хэл → эх хэл (`mn`). Эх хэлээр нөөцлөх нь ХАГАС ДҮҮРЭН
 * толь бичгийг ХОР ХӨНӨӨЛГҮЙ болгодог гол шийдвэр: орчуулаагүй түлхүүр
 * дэлгэц дээр `hr.employees.title` гэж гарахын оронд бодит монгол үг болж гарна.
 */
export const lookup = (locale: UiLocale, key: string, params?: Record<string, unknown>): string | undefined => {
  const found =
    entry(DICTIONARIES[locale], key, params) ??
    (locale === DEFAULT_UI_LOCALE ? undefined : entry(DICTIONARIES[DEFAULT_UI_LOCALE], key, params))

  return found === undefined ? undefined : interpolate(found, params)
}
